/**
 * @fileoverview QuicksearchDialog — outer modal that fronts Foundry's
 * Quicksearch (the Ctrl+J global-search overlay). Renders the
 * `bp6-dialog` shell + the nested scaffold/container layers from
 * `quicksearch-dialog__*`. Children are the input + filters + results
 * sections + footer.
 *
 * Foundry mapping: `quicksearch-dialog__area > __dialog >
 * __content > __offset > __scaffold` (collapsed here into one wrapper).
 */

import type { ReactElement, ReactNode } from "react";

interface QuicksearchDialogProps {
  open: boolean;
  onClose?: () => void;
  /** Input + filters + results stack. */
  children: ReactNode;
  /** Hotkeys footer slot (typically <QuicksearchFooter />). */
  footer?: ReactNode;
  className?: string;
  /**
   * Overrides the default test id.
   *
   * CommandPalette was already passing `data-testid="command-palette"` and this
   * component dropped it on the floor — the prop was not declared, so the value
   * went nowhere and the DOM kept the generic "quicksearch-dialog". A prop that
   * is accepted at the call site and silently ignored is worse than one that
   * does not exist: it reads as configured behaviour.
   */
  "data-testid"?: string;
}

export function QuicksearchDialog({
  open,
  onClose,
  children,
  footer,
  className,
  "data-testid": testId = "quicksearch-dialog",
}: QuicksearchDialogProps): ReactElement | null {
  if (!open) return null;
  return (
    <div data-testid={testId}
      className={["bp-quicksearch-area", className].filter(Boolean).join(" ")}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* NOT a real Blueprint <Dialog> — a hand-rolled panel. Carrying the
          `bp6-dialog` CLASS pulled in Blueprint's unlayered defaults (500px
          wide, light bg, 4px radius) which beat our @layer rules (cascade trap,
          see project_app_aws_blueprint_unlayered_cascade). Drop it so
          .bp-quicksearch-dialog wins cleanly; add `bp6-dark` so the input /
          pills / recent / footer render dark like the Foundry reference. */}
      <div className="bp-quicksearch-dialog bp6-dark" tabIndex={0}>
        <div className="bp-quicksearch-content">
          <div className="bp-quicksearch-stack">{children}</div>
        </div>
        {footer && <div className="bp-quicksearch-footer">{footer}</div>}
      </div>
    </div>
  );
}
